import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AI_FEEDBACK_TEXT_MAX,
  AiFeedbackReason,
  AiRunFeedbackPayload,
  AiRunFeedbackState,
} from '../../core/models/ai.model';
import { AiInsightsService, AiRunService } from '../../core/services/ai-api.service';
import { parseAiHttpError } from '../../core/utils/ai-form.util';
import { feedbackPayload, feedbackProblem } from './ai-run-feedback.util';

/** Avaliação enviada: o que foi gravado e o estado devolvido pela API. */
export interface AiRunFeedbackSaved {
  payload: AiRunFeedbackPayload;
  state: AiRunFeedbackState;
  /** Rótulo do motivo escolhido (para quem quiser reaproveitar o texto, ex.: nova análise). */
  reasonLabel: string | null;
}

/**
 * Avaliação do resultado de uma execução de IA (chamado 0000579137).
 *
 * `rate`: útil / não útil. "Útil" grava na hora; "não útil" abre a lista fixa de motivos (escolha
 * obrigatória) e um complemento opcional de até 300 caracteres (obrigatório em "Outro").
 * `reject`: só o painel de motivo, para a recusa das ações propostas (`rejected_actions`).
 *
 * Ícones do PrimeIcons, como o resto do módulo; o botão marcado usa a variante `-fill`.
 */
@Component({
  selector: 'ai-run-feedback',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['../../shared/ai-form.scss'],
  template: `
    <div class="ai-feedback" [class.ai-feedback--reject]="mode() === 'reject'">
      @if (mode() === 'rate') {
        <div class="ai-feedback__row">
          <span class="ai-feedback__question">O resultado foi útil?</span>
          <button
            type="button"
            class="ai-btn ai-btn--sm"
            [class.is-active]="current() === true"
            [disabled]="saving()"
            [attr.aria-pressed]="current() === true"
            (click)="rateUseful()">
            <i
              [class]="current() === true ? 'pi pi-thumbs-up-fill' : 'pi pi-thumbs-up'"
              aria-hidden="true"></i>
            Útil
          </button>
          <button
            type="button"
            class="ai-btn ai-btn--sm"
            [class.is-active-danger]="current() === false"
            [disabled]="saving()"
            [attr.aria-pressed]="current() === false"
            (click)="openReasons()">
            <i
              [class]="current() === false ? 'pi pi-thumbs-down-fill' : 'pi pi-thumbs-down'"
              aria-hidden="true"></i>
            Não útil
          </button>
          @if (saving()) {
            <i class="pi pi-spin pi-spinner ai-muted" aria-label="Salvando"></i>
          } @else if (savedLabel(); as label) {
            <span class="ai-muted">{{ label }}</span>
          }
        </div>
      }

      @if (reasonsOpen()) {
        <div class="ai-feedback__panel">
          <span class="ai-feedback__question">
            {{ mode() === 'reject' ? 'Por que recusar o resultado?' : 'O que não ficou bom?' }}
            <span class="required"></span>
          </span>
          @if (reasonsError()) {
            <small class="ai-feedback__error">{{ reasonsError() }}</small>
          }
          <div class="ai-feedback__reasons" role="radiogroup" aria-label="Motivo">
            @for (reason of reasons(); track reason.code) {
              <button
                type="button"
                role="radio"
                class="ai-chip ai-feedback__reason"
                [class.is-selected]="reasonCode() === reason.code"
                [attr.aria-checked]="reasonCode() === reason.code"
                [title]="reason.description"
                (click)="reasonCode.set(reason.code)">
                {{ reason.label }}
              </button>
            }
          </div>
          <label class="ai-feedback__label" for="ai-feedback-text-{{ uid }}">
            Detalhe {{ reasonCode() === 'outro' ? '' : '(opcional)' }}
          </label>
          <textarea
            id="ai-feedback-text-{{ uid }}"
            class="p-inputtext ai-feedback__text"
            rows="2"
            [maxLength]="max"
            [ngModel]="reasonText()"
            (ngModelChange)="reasonText.set($event)"
            placeholder="Ex.: a causa indicada não se aplica a esta versão."></textarea>
          <div class="ai-feedback__row">
            <small class="ai-muted">{{ reasonText().length }}/{{ max }}</small>
            <span class="ai-actions__spacer"></span>
            <button
              type="button"
              class="ai-btn ai-btn--sm ai-btn--ghost"
              [disabled]="saving()"
              (click)="closeReasons()">
              Cancelar
            </button>
            <button
              type="button"
              class="ai-btn ai-btn--sm"
              [class.ai-btn--danger]="mode() === 'reject'"
              [class.ai-btn--primary]="mode() !== 'reject'"
              [disabled]="saving() || !!problem()"
              [title]="problem() ?? ''"
              (click)="sendNegative()">
              <i
                [class]="saving() ? 'pi pi-spin pi-spinner' : 'pi pi-send'"
                aria-hidden="true"></i>
              {{ mode() === 'reject' ? rejectLabel() : 'Enviar avaliação' }}
            </button>
          </div>
        </div>
      }

      @if (error()) {
        <small class="ai-feedback__error">{{ error() }}</small>
      }
    </div>
  `,
  styles: [
    `
      .ai-feedback {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;
        padding: 0.75rem 0.9rem;
        border: 1px solid var(--v-border, #e4e4e7);
        border-radius: 12px;
        background: var(--v-bg-soft, #f8fafc);
      }
      .ai-feedback--reject {
        border-color: color-mix(in srgb, #d4522d 35%, #e4e4e7);
      }
      .ai-feedback__row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      .ai-feedback__question {
        font-size: 0.84rem;
        font-weight: 600;
        color: var(--v-text-primary, #111827);
        margin-right: 0.25rem;
      }
      .ai-feedback__panel {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      .ai-feedback__reasons {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
      }
      .ai-feedback__reason {
        cursor: pointer;
        font-size: 0.78rem;
        padding: 0.3rem 0.7rem;
      }
      .ai-feedback__reason.is-selected {
        border-color: #005cab;
        background: color-mix(in srgb, #005cab 12%, transparent);
        color: #005cab;
      }
      .ai-feedback__label {
        font-size: 0.78rem;
        color: var(--v-text-secondary, #475569);
      }
      .ai-feedback__text {
        width: 100%;
        resize: vertical;
        font-size: 0.84rem;
      }
      .ai-feedback__error {
        color: #d4522d;
      }
      .ai-btn.is-active {
        border-color: #15803d;
        color: #15803d;
      }
      .ai-btn.is-active-danger {
        border-color: #d4522d;
        color: #d4522d;
      }
    `,
  ],
})
export class AiRunFeedbackComponent {
  private readonly runs = inject(AiRunService);
  private readonly insights = inject(AiInsightsService);
  private static seq = 0;

  readonly runId = input.required<string>();
  readonly mode = input<'rate' | 'reject'>('rate');
  /** Texto do botão de confirmação no modo `reject`. */
  readonly rejectLabel = input('Recusar e revisar');
  readonly saved = output<AiRunFeedbackSaved>();
  /** Modo `reject`: o usuário desistiu de recusar. */
  readonly cancelled = output<void>();

  readonly uid = ++AiRunFeedbackComponent.seq;
  readonly max = AI_FEEDBACK_TEXT_MAX;
  readonly state = signal<AiRunFeedbackState | null>(null);
  readonly reasons = signal<AiFeedbackReason[]>([]);
  readonly reasonsError = signal<string | null>(null);
  readonly reasonsOpen = signal(false);
  readonly reasonCode = signal<string | null>(null);
  readonly reasonText = signal('');
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  readonly current = computed(() => this.state()?.mine?.useful ?? null);
  readonly problem = computed(() => feedbackProblem(this.reasonCode(), this.reasonText()));
  readonly savedLabel = computed(() => {
    const mine = this.state()?.mine;
    if (!mine) return null;
    return mine.useful
      ? 'Avaliação registrada. Obrigado!'
      : `Registrado: ${mine.reason_label ?? 'não útil'}.`;
  });

  constructor() {
    effect(() => {
      const id = this.runId();
      const mode = this.mode();
      untracked(() => this.load(id, mode));
    });
  }

  private async load(id: string, mode: 'rate' | 'reject'): Promise<void> {
    this.error.set(null);
    this.reasonCode.set(null);
    this.reasonText.set('');
    this.reasonsOpen.set(mode === 'reject');
    this.state.set(null);
    this.loadReasons();
    if (mode === 'rate') {
      try {
        const state = await this.runs.feedback(id);
        if (this.runId() === id) this.state.set(state);
      } catch {
        // Sem a avaliação anterior a tela segue: avaliar de novo substitui.
      }
    }
  }

  private async loadReasons(): Promise<void> {
    try {
      this.reasons.set(await this.insights.feedbackReasons());
      this.reasonsError.set(null);
    } catch (error) {
      this.reasonsError.set(parseAiHttpError(error, 'Não foi possível carregar os motivos.').message);
    }
  }

  openReasons(): void {
    const mine = this.state()?.mine;
    this.reasonCode.set(mine && !mine.useful ? (mine.reason_code ?? null) : null);
    this.reasonText.set(mine && !mine.useful ? (mine.reason_text ?? '') : '');
    this.reasonsOpen.set(true);
  }

  closeReasons(): void {
    if (this.mode() === 'reject') {
      this.cancelled.emit();
      return;
    }
    this.reasonsOpen.set(false);
  }

  rateUseful(): Promise<void> {
    this.reasonsOpen.set(false);
    return this.send({ useful: true });
  }

  sendNegative(): Promise<void> {
    if (this.problem()) return Promise.resolve();
    return this.send(
      feedbackPayload(this.reasonCode()!, this.reasonText(), this.mode() === 'reject')
    );
  }

  private async send(payload: AiRunFeedbackPayload): Promise<void> {
    this.saving.set(true);
    this.error.set(null);
    try {
      const state = await this.runs.sendFeedback(this.runId(), payload);
      this.state.set(state);
      if (this.mode() === 'rate') this.reasonsOpen.set(false);
      const reasonLabel =
        this.reasons().find(reason => reason.code === payload.reason_code)?.label ?? null;
      this.saved.emit({ payload, state, reasonLabel });
    } catch (error) {
      this.error.set(parseAiHttpError(error, 'Não foi possível registrar a avaliação.').message);
    } finally {
      this.saving.set(false);
    }
  }
}
